import { useEffect, useState } from "react";
import { X, Loader2, TrendingUp } from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../../lib/axios";

interface Summary {
  source: string;
  paymentMethod: string;
  revenue: number;
  milestones: { count: number; total: number; released: number; escrow: number; pending: number };
  labour: { source: string; estimatedCost: number; actualCost: number; additionalCost: number; used: number };
  connectsCost: number;
  fees: {
    upworkFeePct: number; upworkFeeAmount: number;
    accountCommissionPct: number; accountCommissionAmount: number;
    bdCommissionPct: number; bdCommissionAmount: number;
  };
  netProfit: number;
  marginPct: number;
  targetMarginPct: number;
  profitStatus: "Profit" | "Loss" | "Below Target";
  payment: { received: number; outstanding: number; progressPct: number };
}

const statusStyle = (s: string) => {
  if (s === "Loss") return { chip: "bg-rose-100 text-rose-700 border-rose-200", dot: "bg-rose-500" };
  if (s === "Below Target") return { chip: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500" };
  return { chip: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" };
};

const usd = (n: number) =>
  `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ProjectSummaryModal({
  projectId,
  projectName,
  onClose,
}: {
  projectId: string;
  projectName: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get(`/project/${projectId}/summary`);
        setData(res.data);
      } catch {
        toast.error("Failed to load financial summary");
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl max-h-[92vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <TrendingUp size={18} className="text-[#0058c3]" /> Financial Summary
            </h2>
            <p className="text-[12px] text-slate-500">{projectName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-full"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto px-6 py-5 flex-1">
          {loading || !data ? (
            <div className="flex items-center justify-center gap-2 py-12 text-slate-500">
              <Loader2 size={18} className="animate-spin text-[#0058c3]" /> Loading…
            </div>
          ) : (
            <div className="space-y-5">
              {/* headline */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
                  <div className="text-[11px] font-semibold text-blue-700 uppercase">Total Revenue</div>
                  <div className="text-2xl font-extrabold text-blue-900 mt-1">{usd(data.revenue)}</div>
                  <div className="text-[11px] text-blue-600 mt-1">{data.source}{data.paymentMethod ? ` · ${data.paymentMethod}` : ""}</div>
                </div>
                <div className={`rounded-xl p-4 border ${data.netProfit >= 0 ? "bg-emerald-50 border-emerald-100" : "bg-rose-50 border-rose-100"}`}>
                  <div className="flex items-center justify-between">
                    <div className={`text-[11px] font-semibold uppercase ${data.netProfit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>Net Profit</div>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${statusStyle(data.profitStatus).chip}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusStyle(data.profitStatus).dot}`} /> {data.profitStatus}
                    </span>
                  </div>
                  <div className={`text-2xl font-extrabold mt-1 ${data.netProfit >= 0 ? "text-emerald-900" : "text-rose-900"}`}>{usd(data.netProfit)}</div>
                  <div className="text-[11px] text-slate-600 mt-1 flex items-center gap-2">
                    <span className="font-bold">{data.marginPct}% margin</span>
                    {data.targetMarginPct > 0 && (
                      <span className="text-slate-400">· target {data.targetMarginPct}%</span>
                    )}
                  </div>
                </div>
              </div>

              {/* cost breakdown */}
              <Section title="Cost Breakdown">
                <Row label="Labour Cost" value={`− ${usd(data.labour.used)}`} note={data.labour.source === "erp" ? "ERP rates" : "config"} />
                {data.labour.additionalCost > 0 && (
                  <Row label="↳ incl. Overtime" value={usd(data.labour.additionalCost)} danger />
                )}
                <Row label={`Upwork Fee (${data.fees.upworkFeePct}%)`} value={`− ${usd(data.fees.upworkFeeAmount)}`} />
                <Row label={`Account Commission (${data.fees.accountCommissionPct}%)`} value={`− ${usd(data.fees.accountCommissionAmount)}`} />
                <Row label={`BD Commission (${data.fees.bdCommissionPct}%)`} value={`− ${usd(data.fees.bdCommissionAmount)}`} />
                <Row label="Connects Cost" value={`− ${usd(data.connectsCost)}`} />
                <div className="border-t border-slate-200 mt-1 pt-1">
                  <Row label="Net Profit" value={usd(data.netProfit)} bold />
                </div>
              </Section>

              {/* milestones */}
              {data.milestones.count > 0 && (
                <Section title={`Milestones (${data.milestones.count})`}>
                  <Row label="Total" value={usd(data.milestones.total)} />
                  <Row label="Released" value={usd(data.milestones.released)} />
                  <Row label="In Escrow" value={usd(data.milestones.escrow)} />
                  <Row label="Pending" value={usd(data.milestones.pending)} />
                </Section>
              )}

              {/* payment progress */}
              <Section title="Payment Progress">
                <div className="mb-2">
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-[#0058c3]" style={{ width: `${data.payment.progressPct}%` }} />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">{data.payment.progressPct}% received</div>
                </div>
                <Row label="Received" value={usd(data.payment.received)} />
                <Row label="Outstanding" value={usd(data.payment.outstanding)} />
              </Section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <h3 className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">{title}</h3>
    <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-3 space-y-1.5">{children}</div>
  </div>
);

const Row = ({ label, value, note, bold, danger }: { label: string; value: string; note?: string; bold?: boolean; danger?: boolean }) => (
  <div className="flex items-center justify-between text-sm">
    <span className={`${bold ? "font-bold text-slate-900" : "text-slate-600"}`}>
      {label}{note ? <span className="text-[10px] text-slate-400 ml-1">({note})</span> : null}
    </span>
    <span className={`${bold ? "font-extrabold text-slate-900" : danger ? "text-rose-600 font-semibold" : "font-medium text-slate-800"}`}>{value}</span>
  </div>
);
